import { COLORS, TYPOGRAPHY } from '@app/constants';
import { useHasMounted } from '@app/hooks/useHasMounted';
import { DOTS, usePagination } from '@app/hooks/usePagination';
import { BreakpointsEnum } from '@app/types';
import { FC, Fragment } from 'react';
import styled, { css } from 'styled-components';

interface Props {
  currentPage: number;
  currentUrl?: string;
  totalItemCount: number;
  pageItemCount?: number;
  initialTotalPage?: number;
  className?: string;
  onFlip: (page: number) => void;
}

const Pagination: FC<Props> = ({
  currentPage,
  currentUrl,
  className,
  totalItemCount,
  pageItemCount = 10,
  initialTotalPage,
  onFlip,
}) => {
  const hasMounted = useHasMounted();

  const paginationRange = usePagination({
    currentPage,
    totalItemCount,
    pageItemCount,
    initialTotalPageCount: initialTotalPage,
  });

  if (!paginationRange || paginationRange.length < 2) {
    return null;
  }

  const lastPage = paginationRange[paginationRange.length - 1];

  function generateSsrUrl(pageNumber: number): string {

    const [ path, searchString ] = (currentUrl || '').split('?')

    const params = new URLSearchParams(searchString)

    if (params.has('page')) {
      params.delete('page');
    }

    if (pageNumber > 1) {
      params.append('page', pageNumber.toString())
    }

    return [ path, params.toString() ].join('?');
  }

  return (
    <Root>
      {currentPage > 1 && hasMounted && (
        <PreviousPageBtn onClick={() => onFlip(currentPage - 1)}>
          Предыдущая
        </PreviousPageBtn>
      )}

      <List className={className}>
        {paginationRange.map((item, index) => {
          const isCurrent = item === currentPage;

          if (item === DOTS) {
            return <ListItem key={index}>&#8230;</ListItem>;
          }

          return (
            <Fragment key={index}>
              {currentUrl && (
                <noscript>
                  <a
                    href={`${generateSsrUrl(item as number)}`}
                    style={{ display: 'block', marginRight: '10px' }}
                  >
                    {item}
                  </a>{' '}
                  <br />
                </noscript>
              )}

              {hasMounted && (
                <ListItem>
                  <PageBtn
                    onClick={() => !isCurrent && onFlip(item as number)}
                    $isCurrent={isCurrent}
                  >
                    {item}
                  </PageBtn>
                </ListItem>
              )}
            </Fragment>
          );
        })}
      </List>

      {+currentPage < +lastPage && hasMounted && (
        <NextPageBtn onClick={() => onFlip(currentPage + 1)}>
          Следующая
        </NextPageBtn>
      )}
    </Root>
  );
};

export default Pagination;

const Root = styled.div`
  display: flex;
  flex-wrap: wrap;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    flex-direction: column;
    align-items: center;
  }
`;

const List = styled.ol`
  display: flex;
  flex-wrap: wrap;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    margin-bottom: 5px;
  }
`;

const ListItem = styled.li`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  &:not(:first-child) {
    margin-left: 2px;
  }
`;

interface PageBtnStyleProps {
  $isCurrent?: boolean;
}

const PageBtn = styled.button<PageBtnStyleProps>`
  ${TYPOGRAPHY.body1Medium14}
  color: ${COLORS.grayscale700};
  min-width: 40px;
  min-height: 40px;
  padding: 5px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;

  background-color: transparent;
  line-height: normal;
  cursor: pointer;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    min-width: 45px;
    min-height: 45px;
  }

  ${({ $isCurrent }) =>
    $isCurrent
      ? css`
          color: ${COLORS.primary300};
          border: 1px solid ${COLORS.primary300};
          cursor: default;
        `
      : css`
          &:hover {
            color: ${COLORS.primary300};
          }
          &:active {
            color: ${COLORS.primary300};
          }
        `};
`;

const PreviousPageBtn = styled(PageBtn)`
  border-radius: 5px;

  @media (min-width: ${BreakpointsEnum.sm}px) {
    margin-right: 20px;
  }
  @media (max-width: ${BreakpointsEnum.sm}px) {
    align-self: stretch;
    margin-bottom: 5px;
  }
`;

const NextPageBtn = styled(PageBtn)`
  border-radius: 5px;

  @media (min-width: ${BreakpointsEnum.sm}px) {
    margin-left: 20px;
  }
  @media (max-width: ${BreakpointsEnum.sm}px) {
    align-self: stretch;
  }
`;
